import { MouseEvent, RefObject, useEffect, useState } from 'react';
import { prop } from 'ramda';
import { debounce } from 'underscore';

import { useDeviceSelector } from '@core/js/store/hooks';

import { hasClass, isCorrectType } from '@interlayer/js/custom/components/main/BingoScheduler/helpers/utilities';

import style from '../BingoScheduler.scss';

interface Props {
    bingoListRef: RefObject<HTMLUListElement>;
}

const DIRECTION = 'next';

const useGetBingoControls = ({ bingoListRef }: Props) => {
    const [oldPosition, setOldPosition] = useState(0);
    const [currentPosition, setCurrentPosition] = useState(0);
    const [startPageX, setStartPageX] = useState(0);
    const [isLastView, setLastView] = useState(false);
    const formFactor = useDeviceSelector(prop('formFactor'));

    const debouncedSetCurrentPosition = debounce((newPosition: number) => {
        setCurrentPosition(newPosition);
    }, 150);

    const isDesktop = formFactor === 'desktop';
    const THRESHOLD = 0.15;
    const schedulerActionsClass = style.bingo_scheduler__actions;
    const schedulerActionPlayClass = style.bingo_scheduler__actions_play;

    useEffect(() => {
        const { current: bingoList } = bingoListRef;

        if (!bingoList) {
            return;
        }

        const { style: listStyle, scrollWidth, clientWidth } = bingoList;

        listStyle.transform = `translateX(-${currentPosition}px)`;

        setLastView(scrollWidth - currentPosition <= clientWidth);
    }, [bingoListRef, currentPosition]);

    const getClickHandlerByDirection = (direction: string) => () => {
        const { current: bingoList } = bingoListRef;

        if (!bingoList) {
            return;
        }

        const { clientWidth, scrollWidth } = bingoList;
        const maxPosition = scrollWidth - clientWidth;
        const step: 1 | -1 = direction === DIRECTION ? 1 : -1;
        const clampedPosition = Math.min(maxPosition, Math.max(0, currentPosition + step * clientWidth));

        debouncedSetCurrentPosition(clampedPosition);
    };

    const onMouseDownHandler = (e: MouseEvent<HTMLUListElement>) => {
        if (isCorrectType(e, 'button') || hasClass(e, schedulerActionsClass) || hasClass(e, schedulerActionPlayClass)) {
            return;
        }

        setOldPosition(currentPosition);
        setStartPageX(e.clientX);
    };

    const onMouseMoveHandler = (e: MouseEvent<HTMLUListElement>) => {
        const { current: bingoList } = bingoListRef;
        const { clientX, buttons } = e;

        if (!bingoList) {
            return;
        }

        const { clientWidth, scrollWidth } = bingoList;

        if (!buttons || hasClass(e, schedulerActionsClass) || hasClass(e, schedulerActionPlayClass)) {
            return;
        }

        const isDraggedForward = startPageX - clientX > clientWidth * THRESHOLD;
        const isDraggedBackward = clientX - startPageX > clientWidth * THRESHOLD;

        const newCurrentPosition = startPageX > clientX
            ? oldPosition + clientWidth
            : oldPosition - clientWidth;

        const maxPosition = scrollWidth - clientWidth;
        const clampedPosition = Math.min(maxPosition, Math.max(0, newCurrentPosition));

        if ((!isLastView || startPageX > clientX) && isDraggedForward) {
            debouncedSetCurrentPosition(clampedPosition);
            return;
        }

        if (currentPosition > 0 && isDraggedBackward) {
            debouncedSetCurrentPosition(clampedPosition);
        }
    };

    if (!isDesktop) {
        return {
            prevClickHandler: undefined,
            nextClickHandler: undefined,
            onMouseDownHandler: undefined,
            onMouseMoveHandler: undefined,
            currentPosition,
            isLastView
        };
    }

    return {
        prevClickHandler: getClickHandlerByDirection('prev'),
        nextClickHandler: getClickHandlerByDirection('next'),
        onMouseDownHandler,
        onMouseMoveHandler,
        currentPosition,
        setCurrentPosition,
        isLastView
    };
};

export default useGetBingoControls;
